<template>
  <b-card no-body class="my-4 shadow" :class="account.is_sub ? 'mr-5 ml-5' : ''">
    <template v-slot:header>
      <b-row>
        <b-col lg="3">
          <h4>
            <MgrLine v-if="account" :login_name_only="true" :disable_svc_link="true" :small="true" :mgr="account"/>
          </h4>
          <p class="text-muted mb-0">{{ $t('views.netdb.tokens.login_name') }}</p>
        </b-col>
        <b-col v-if="account.first_name">
          <div>{{ account.first_name }}</div>
          <p class="text-muted">{{ $t('system.firstname') }}</p>
        </b-col>
        <b-col v-if="account.last_name">
          <div>{{ account.last_name }}</div>
          <p class="text-muted">{{ $t('system.lastname') }}</p>
        </b-col>
        <b-col v-if="account.is_sub && account.sub_id">
          <div>{{ account.sub_id }}</div>
          <p class="text-muted">{{ $t('components.svc_card.sub_id') }}</p>
        </b-col>
        <b-col>
          <div v-if="!account.description">
            {{ $t('views.netdb.tokens.no_description_found') }}
          </div>
          <div v-else>{{ account.description }}</div>
          <p class="text-muted">{{ $t('system.description') }}</p>
        </b-col>
        <b-col>
          <template v-if="account_roles && account_roles.length">
            <h5 class="mb-0">
              <span
                v-for="role in expand_roles ? account_roles : account_roles.slice(0, max_role_badge_count)"
                :key="'role-badge-' + role.mgr_login_name + '-' + role.role_fq_name + _uid">
                <b-button
                  :id="uid('role-badge-' + role.mgr_login_name + '-' + role.role_fq_name)"
                  :style="{background: role.role_fq_name.toHSL({lit: [30, 40]}), border: 'none'}"
                  class="mr-1 mb-1 badge" tabindex="0" href="#">{{ role.system.toUpperCase() }}<br>{{
                    role.role
                  }}
                </b-button>
                <b-popover
                  :target="uid('role-badge-' + role.mgr_login_name + '-' + role.role_fq_name)"
                  triggers="focus" custom-class="popover-wide"
                  placement="bottom">
                  <b-table :fields="permission_list_fields"
                           :items="Object.values(permissions).filter(p => (p.hierarchy_gfk_list || []).includes(subroles[role.role_fq_name]?.gpk))"
                           :sticky-header="true"/>
                </b-popover>
              </span>
            </h5>
            <template v-if="account_roles.length > max_role_badge_count">
              <b-link @click="expand_roles = true" v-if="!expand_roles">
                + {{ account_roles.length - max_role_badge_count }}
                {{ $t('views.netdb.tokens.additional') }}
              </b-link>
              <b-link @click="expand_roles = false" v-else>{{ $t('views.netdb.tokens.show_less') }}
              </b-link>
            </template>
          </template>
          <h6 v-else>{{ $t('views.netdb.tokens.no_roles_assigned') }}</h6>
          <p class="text-muted">{{ $t('views.netdb.tokens.roles_global_rights') }}</p>
        </b-col>
        <b-col lg="2">
          <template v-if="show_action_buttons">
            <b-button-group class="d-flex">
              <b-button v-if="show_impersonate_button"
                        :variant="can_impersonate_normally ? 'outline-secondary' : 'outline-danger'"
                        :id="uid('impersonate')"
                        @click="$store.commit('updateImpersonatingUser', account.login_name); $store.commit('reloadRouterComp')">
                <netvs-icon icon="impersonate"></netvs-icon>
              </b-button>
              <b-tooltip v-if="show_impersonate_button" :target="uid('impersonate')" triggers="hover"
                         :variant="can_impersonate_normally ? 'secondary' : 'danger'"
                         placement="bottom">
                {{
                  can_impersonate_normally ? $t('components.svc_card.impersonate') : $t('components.svc_card.impersonate_rollback')
                }}
              </b-tooltip>
              <b-button v-if="show_evlog !== undefined" variant="outline-secondary"
                        :id="uid('evlog')"
                        @click="show_evlog(account)">
                <netvs-icon icon="evlog"></netvs-icon>
              </b-button>
              <b-tooltip v-if="show_evlog !== undefined" :target="uid('evlog')" triggers="hover" variant="secondary"
                         placement="bottom">
                {{
                  $t('system.event_log')
                }}
              </b-tooltip>
              <b-button variant="outline-primary"
                        :id="uid('edit-account')"
                        @click="edit_account(account)">
                <netvs-icon icon="edit"></netvs-icon>
              </b-button>
              <b-tooltip placement="bottom" :target="uid('edit-account')"
                         triggers="hover" variant="primary">
                {{ $t('system.utils.update_template', { object: $t('system.account') }) }}
              </b-tooltip>
              <b-button v-if="$store.state.expert" variant="outline-secondary"
                        :id="uid('edit-account-api')"
                        @click="editAccountApi()">
                <netvs-icon icon="edit_api"></netvs-icon>
              </b-button>
              <b-tooltip v-if="$store.state.expert"
                         placement="bottom" :target="uid('edit-account-api')"
                         triggers="hover" variant="secondary">
                {{ $t('system.utils.update_template', { object: $t('system.account') }) }} (API)
                <NetvsExpertMarker class="ml-1"></NetvsExpertMarker>
              </b-tooltip>
              <b-button :disabled="$store.state.token.login_name === account.login_name"
                        @click="delete_mgr(account)"
                        variant="outline-danger"
                        :id="uid('delete-account')">
                <netvs-icon icon="delete"></netvs-icon>
              </b-button>
              <b-tooltip :target="uid('delete-account')" triggers="hover"
                         variant="danger" placement="bottom">
                {{ $t('system.utils.delete_template', { object: $t('system.account') }) }}
              </b-tooltip>
            </b-button-group>
          </template>
        </b-col>
      </b-row>
    </template>
    <template v-if="(!account.is_svc || account.is_sub) && (!parent_is_svc || impersonating_parent)">
      <b-button block squared variant="outline-secondary" v-b-toggle="uid('tokens-collapse')">
        <netvs-icon icon="token"></netvs-icon>
        {{ $tc('system.token', 2) }}
        <b-badge pill v-if="account_tokens">{{
            account_tokens.length
          }}
        </b-badge>
        <b-badge pill v-else>0</b-badge>
        <br/>
        <netvs-icon class="collapse-icon" icon="collapse"></netvs-icon>
      </b-button>
      <b-collapse :id="uid('tokens-collapse')">
        <b-table :items="account_tokens"
                 :fields="token_list_fields"
                 class="m-0"
                 striped
                 :responsive="true"
                 primary-key="description">
          <template v-slot:head(buttons)>
            <template v-if="account.parent_login_name !== account.login_name">
              <b-button block variant="outline-success"
                        :id="uid('create-token')"
                        @click="create_token(account.login_name)">
                <netvs-icon icon="create"></netvs-icon>
              </b-button>
              <b-tooltip :target="uid('create-token')" triggers="hover"
                         variant="success" placement="left">
                {{ $t('system.utils.create_template', { object: $tc('system.token', 1) }) }}
              </b-tooltip>
            </template>
          </template>
          <template v-slot:cell(expiration_date)="data">
            {{ formatDate(data.item.expiration_date) }}
            <template v-if="data.item.is_expired">
              {{ $t('views.netdb.tokens.expired') }}
            </template>
          </template>
          <template v-slot:cell(description)="data">
            {{ data.item.description }}
            <b-badge :id="uid('badge-token-' + data.item.gpk)" variant="primary">{{ data.item.type }}</b-badge>
            <b-tooltip :target="uid('badge-token-' + data.item.gpk)"
                       triggers="hover" variant="primary" placement="right">
              {{ data.item.type_description }}
            </b-tooltip>
            <b-badge v-if="data.item.gpk === $store.state.token?.gpk" variant="success"
                     :title="$t('views.netdb.tokens.current_session_token')">
              {{ $t('views.netdb.tokens.current_session_token_short') }}
            </b-badge>
          </template>
          <template v-slot:cell(buttons)="data">
            <b-button-group class="d-flex">
              <b-button @click="delete_token(data.item)" variant="outline-danger"
                        :id="uid('delete-token-' + data.item.gpk)">
                <netvs-icon icon="delete"></netvs-icon>
              </b-button>
              <b-tooltip :target="uid('delete-token-' + data.item.gpk)" triggers="hover"
                         variant="danger" placement="left">
                {{ $t('system.utils.delete_template', { object: $tc('system.token', 1) }) }}
              </b-tooltip>
              <b-button variant="outline-primary" v-if="account.parent_login_name !== account.login_name"
                        :id="uid('edit-token-' + data.index)"
                        @click="edit_token(data.item)">
                <netvs-icon icon="edit"></netvs-icon>
              </b-button>
              <b-tooltip :target="uid('edit-token-' + data.index)"
                         triggers="hover" variant="primary" placement="left"
                         v-if="account.parent_login_name !== account.login_name">
                {{ $t('system.utils.update_template', { object: $tc('system.token', 1) }) }}
              </b-tooltip>
            </b-button-group>
          </template>
        </b-table>
      </b-collapse>
    </template>
    <b-button block squared variant="outline-secondary" v-if="!account.is_sub"
              v-b-toggle="uid('ous-collapse')">
      <netvs-icon icon="orgs"></netvs-icon>
      {{ $tc('system.organizational_unit', 2) }}
      <b-badge pill v-if="account.login_name in ous_by_mgr">{{
          ous_by_mgr[account.login_name].length
        }}
      </b-badge>
      <b-badge pill v-else>0</b-badge>
      <br/>
      <netvs-icon class="collapse-icon" icon="collapse"></netvs-icon>
    </b-button>
    <b-collapse v-if="!account.is_sub" :id="uid('ous-collapse')">
      <b-alert v-if="account.is_svc" show class="m-3">
        <netvs-icon icon="info"></netvs-icon>
        {{ $t('components.svc_card.ou_svc_info') }}
      </b-alert>
      <b-table :items="ous_by_mgr[account.login_name] || []"
               :fields="ou_list_fields"
               class="m-0"
               striped
               :responsive="true"
               primary-key="ou_fq_name">
        <template v-slot:head(buttons)>
          <b-button block variant="outline-success"
                    :id="uid('create-mgr2ou')"
                    @click="createMgr2OU()">
            <netvs-icon icon="create"></netvs-icon>
          </b-button>
          <b-tooltip :target="uid('create-mgr2ou')" triggers="hover"
                     variant="success" placement="left">
            {{ $t('views.org.ou.assign_account') }}
          </b-tooltip>
        </template>
        <template v-slot:cell(ou_fq_name)="data">
          <b-link :to="`/org/ou/${data.item.ou_fq_name}`">{{ data.item.ou_fq_name }}</b-link>
        </template>
        <template v-slot:cell(buttons)="data">
          <b-button-group class="d-flex">
            <b-button v-if="(ous_by_mgr[account.login_name] || []).length > 1 || $store.state.expert"
                      @click="delete_mgr2ou(data.item)" variant="outline-danger"
                      :id="uid('delete-mgr2ou-' + data.item.gpk)">
              <netvs-icon icon="unlink"></netvs-icon>
              <NetvsExpertMarker v-if="(ous_by_mgr[account.login_name] || []).length <= 1 && $store.state.expert"
                                 class="ml-1"></NetvsExpertMarker>
            </b-button>
            <b-tooltip :target="uid('delete-mgr2ou-' + data.item.gpk)" triggers="hover"
                       variant="danger" placement="left">
              {{ $t('views.org.ou.remove_assignment') }}
            </b-tooltip>
          </b-button-group>
        </template>
      </b-table>
    </b-collapse>
    <b-button block squared variant="outline-secondary" v-b-toggle="uid('groups-collapse')">
      <netvs-icon icon="groups"></netvs-icon>
      {{ $tc('system.group', 2) }}
      <b-badge pill v-if="account_groups">{{
          account_groups.length
        }}
      </b-badge>
      <b-badge pill v-else>0</b-badge>
      <br/>
      <netvs-icon class="collapse-icon" icon="collapse"></netvs-icon>
    </b-button>
    <b-collapse :id="uid('groups-collapse')">
      <b-table :items="account_groups" :fields="group_list_fields" class="m-0" striped :responsive="true"
               primary-key="name">
        <template v-slot:cell(name)="data">
          <b-link :to="`/cntl/groups/${encodeURIComponent(data.item.name)}`">{{ data.item.name }}</b-link>
        </template>
        <template v-slot:cell(ou_fq_name)="data">
          <b-link :to="`/org/ou/${data.item.ou_fq_name}`">{{ data.item.ou_fq_name }}</b-link>
        </template>
        <template v-slot:cell(description)="data">
          {{ data.item.description }}
        </template>
        <template v-slot:head(buttons)>
          <b-button block variant="outline-success"
                    :id="uid('create-mgr2group')"
                    @click="createMgr2Group()">
            <netvs-icon icon="create"></netvs-icon>
          </b-button>
          <b-tooltip :target="uid('create-mgr2group')" triggers="hover"
                     variant="success" placement="left">
            {{ $t('components.svc_card.assign_group') }}
          </b-tooltip>
        </template>
        <template v-slot:cell(buttons)="data">
          <b-button-group class="d-flex">
            <b-button @click="delete_mgr2group(data.item)" variant="outline-danger"
                      :id="uid('delete-mgr2group-' + data.item.gpk)">
              <netvs-icon icon="unlink"></netvs-icon>
            </b-button>
            <b-tooltip :target="uid('delete-mgr2group-' + data.item.gpk)" triggers="hover"
                       variant="danger" placement="left">
              {{ $t('components.svc_card.remove_assignment') }}
            </b-tooltip>
          </b-button-group>
        </template>
      </b-table>
    </b-collapse>
    <template v-if="!account.is_sub && (sub_mgr || is_active_user)">
      <b-button block squared variant="outline-secondary" v-b-toggle="uid('subm-collapse')">
        <netvs-icon icon="groups"></netvs-icon>
        {{ $tc('system.subaccount', 2) }}
        <b-badge pill v-if="sub_mgr">{{
            sub_mgr.length
          }}
        </b-badge>
        <b-badge pill v-else>0</b-badge>
        <br/>
        <netvs-icon class="collapse-icon" icon="collapse"></netvs-icon>
      </b-button>
      <b-collapse :id="uid('subm-collapse')">
        <b-button v-if="is_active_user" block squared variant="outline-success" class="mt-3"
                  :id="uid('create-sub-account')"
                  @click="create_sub_account">
          <netvs-icon icon="create"></netvs-icon>
        </b-button>
        <b-tooltip v-if="is_active_user" :target="uid('create-sub-account')" triggers="hover"
                   variant="success" placement="right">
          {{ $t('system.utils.create_template', { object: $tc('system.subaccount', 1) }) }}
        </b-tooltip>
        <AccountCard v-for="mgr in sub_mgr" v-bind:key="mgr.login_name"
                     class="mr-4 ml-4 mt-3"
                     :account="mgr"
                     :subroles="subroles"
                     :permissions="permissions"
                     :roles_by_mgr="roles_by_mgr"
                     :tokens_by_mgr="tokens_by_mgr"
                     :groups_by_mgr="groups_by_mgr"
                     :ous_by_mgr="ous_by_mgr"
                     :sub_mgrs_by_mgr="sub_mgrs_by_mgr"
                     :edit_account="edit_account"
                     :show_evlog="show_evlog"
                     :check_can_impersonate="check_can_impersonate"
                     :own_groups="own_groups"
                     :own_ous="own_ous"
                     :parent_is_svc="account.is_svc"
        />
      </b-collapse>
    </template>
    <APIObjectDBEditor :modal_id="uid('db-editor')" :object_function="db_editor_function"
                       :object_fq_name="db_editor_object_fq_name"
                       :presets="db_editor_presets"
                       :non_optionals_order="db_editor_non_optionals_order"
                       :show_result="db_editor_show_result"
                       :old_data="db_editor_old_data"
                       :typeahead_data_by_attribute="db_editor_typeahead_data_by_attribute">
    </APIObjectDBEditor>
  </b-card>
</template>

<script>
import ApiUtil from '@/util/apiutil'
import dateutil from '@/util/dateutil'
import CntlMgr2ou from '@/api-services.gen/cntl.mgr2ou'
import Transactionutil from '@/util/transactionutil'
import CntlWapi_auth from '@/api-services.gen/cntl.wapi_auth'
import CntlMgr from '@/api-services.gen/cntl.mgr'
import CntlMgr2group from '@/api-services.gen/cntl.mgr2group'
import MgrLine from '@/components/MgrLine.vue'
import NetvsExpertMarker from '@/components/NETVSExpertMarker.vue'
import APIObjectDBEditor from '@/components/db-editor/APIObjectDBEditor.vue'

export default {
  name: 'AccountCard',
  components: { MgrLine, NetvsExpertMarker, APIObjectDBEditor },
  data() {
    return {
      max_role_badge_count: 5,
      expand_roles: false,
      db_editor_object_fq_name: null,
      db_editor_function: 'create',
      db_editor_presets: {},
      db_editor_old_data: {},
      db_editor_show_result: false,
      db_editor_non_optionals_order: [],
      db_editor_typeahead_data_by_attribute: {},
    }
  },
  props: {
    account: {
      type: Object,
      required: true
    },
    groups_by_mgr: {
      type: Object,
      default() {
        return {}
      }
    },
    tokens_by_mgr: {
      type: Object,
      default() {
        return {}
      }
    },
    roles_by_mgr: {
      type: Object,
      default() {
        return {}
      }
    },
    permissions: {
      type: Object,
      required: true
    },
    subroles: {
      type: Object,
      required: true
    },
    edit_account: {
      type: Function,
      required: true
    },
    show_evlog: {
      type: Function,
      required: false,
      default: undefined
    },
    check_can_impersonate: {
      type: Function,
      required: false,
      default: () => false
    },
    own_groups: {
      type: Array,
      default() {
        return []
      }
    },
    own_ous: {
      type: Array,
      default() {
        return []
      }
    },
    parent_is_svc: {
      type: Boolean,
      default() {
        return false
      }
    },
    sub_mgrs_by_mgr: {
      type: Object,
      default() {
        return {}
      }
    },
    ous_by_mgr: {
      type: Object,
      default() {
        return {}
      }
    },
  },
  methods: {
    checkPermission: ApiUtil.checkPermission,
    uid(suffix) {
      return suffix + '-' + this.account.login_name + '-' + this._uid
    },
    is_own_sub() {
      return this.account.parent_login_name !== this.account.login_name
    },
    delete_mgr(mgr) {
      const ta = Transactionutil.generateDeleteElement('cntl.mgr', CntlMgr.deleteParamsList(), mgr, this.$t('system.delete') + ': ' + mgr.login_name)
      this.$store.commit('addTransactionElement', ta)
      this.$emit('commited', ta)
      if (!this.isMobile() && !this.$store.state.show_sidebar_right) {
        this.$store.state.show_sidebar_right = true
      }
    },
    delete_token(tok) {
      const ta = Transactionutil.generateDeleteElement('cntl.wapi_auth_token', CntlWapi_auth.deleteParamsList(), tok, tok.login_name)
      this.$store.commit('addTransactionElement', ta)
      this.$emit('commited', ta)
      if (!this.isMobile() && !this.$store.state.show_sidebar_right) {
        this.$store.state.show_sidebar_right = true
      }
    },
    delete_mgr2ou(acc) {
      const ta = Transactionutil.generateDeleteElement('cntl.mgr2ou', CntlMgr2ou.deleteParamsList(), acc, acc.ou_fq_name + ' from ' + acc.mgr_login_name)
      this.$store.commit('addTransactionElement', ta)
      this.$emit('commited', ta)
      if (!this.isMobile() && !this.$store.state.show_sidebar_right) {
        this.$store.state.show_sidebar_right = true
      }
    },
    delete_mgr2group(group) {
      const item = { mgr_login_name: this.account.login_name, group_name: group.name }
      const ta = Transactionutil.generateDeleteElement('cntl.mgr2group', CntlMgr2group.deleteParamsList(), item, item.mgr_login_name + ' ' + this.$t('views.cntl.groups.from_group') + ' ' + item.group_name)
      this.$store.commit('addTransactionElement', ta)
      this.$emit('commited', ta)
      if (!this.isMobile() && !this.$store.state.show_sidebar_right) {
        this.$store.state.show_sidebar_right = true
      }
    },
    openDBEditor(object_fq_name, function_, presets, non_optionals_order, old_data = {}, show_result = false, typeahead_data_by_attribute = {}) {
      this.db_editor_object_fq_name = object_fq_name
      this.db_editor_function = function_
      this.db_editor_presets = presets
      this.db_editor_old_data = old_data
      this.db_editor_show_result = show_result
      this.db_editor_non_optionals_order = non_optionals_order
      this.db_editor_typeahead_data_by_attribute = typeahead_data_by_attribute
      this.$root.$emit('bv::show::modal', this.uid('db-editor'))
    },
    editAccountApi() {
      let order
      if (this.account.is_svc) {
        order = ['svc_id', 'description', 'allow_data_manipulation']
      } else if (this.account.is_sub) {
        order = ['sub_id', 'description', 'allow_data_manipulation']
      } else {
        order = ['description', 'do_copy_roles', 'allow_data_manipulation']
      }
      this.openDBEditor('cntl.mgr', 'update', this.account, order, this.account)
    },
    createMgr2Group() {
      this.openDBEditor('cntl.mgr2group', 'create', { mgr_login_name: this.account.login_name },
        ['group_name', 'parent_name', 'description', 'do_copy_assignments', 'mgr_login_name', 'bcd_name', 'fqdn_value'],
        {}, false, { group_name: this.own_groups.map(group => group.name) })
    },
    createMgr2OU() {
      this.openDBEditor('cntl.mgr2ou', 'create', { mgr_login_name: this.account.login_name },
        ['ou_fq_name', 'group_name', 'parent_name', 'description', 'do_copy_assignments', 'mgr_login_name', 'bcd_name', 'fqdn_value'],
        {}, false, { ou_fq_name: this.own_ous.map(ou => ou.fq_name) })
    },
    create_sub_account() {
      this.openDBEditor('cntl.mgr', 'create', {},
        ['sub_id', 'description', 'allow_data_manipulation'])
    },
    create_token(login_name) {
      this.openDBEditor('cntl.wapi_auth', 'create', { login_name: login_name },
        ['login_name', 'description', 'expiration_date', 'do_refresh_token'], {}, true)
    },
    edit_token(token) {
      this.openDBEditor('cntl.wapi_auth', 'update', token,
        ['login_name', 'description', 'expiration_date', 'do_refresh_token'], token)
    },
    formatDate(value) {
      if (value == null) {
        return 'N/A'
      }
      return dateutil.format_date(new Date(Date.parse(value)))
    },
  },
  computed: {
    is_active_user() {
      return this.account.login_name === (this.$store.state.impersonate_user || this.$store.state.user?.login_name)
    },
    impersonating_parent() {
      return this.account.parent_login_name === this.$store.state.impersonate_user
    },
    show_action_buttons() {
      if (this.account.is_svc) {
        return true
      }
      return this.checkPermission(this.$store.state, 'cntl.admin') || this.is_own_sub()
    },
    show_impersonate_button() {
      if (this.account.login_name === (this.$store.state.impersonate_user || this.$store.state.user?.login_name)) {
        return false
      }
      return this.can_impersonate_normally || this.checkPermission(this.$store.state, 'cntl.admin') || this.$store.state.developer
    },
    can_impersonate_normally() {
      if (this.account.is_svc) {
        return this.check_can_impersonate(this.account.login_name)
      }
      return this.is_own_sub()
    },
    sub_mgr() {
      if (this.account.login_name in this.sub_mgrs_by_mgr) {
        return this.sub_mgrs_by_mgr[this.account.login_name]
      }
      return null
    },
    account_groups() {
      if (this.account.login_name in this.groups_by_mgr) {
        return this.groups_by_mgr[this.account.login_name]
      }
      return []
    },
    account_tokens() {
      if (this.account.login_name in this.tokens_by_mgr) {
        return this.tokens_by_mgr[this.account.login_name]
      }
      return []
    },
    account_roles() {
      if (this.account.login_name in this.roles_by_mgr) {
        return this.roles_by_mgr[this.account.login_name]
      }
      return []
    },
    ou_list_fields() {
      return [
        {
          key: 'ou_fq_name',
          label: this.$tc('system.organizational_unit', 1)
        },
        {
          key: 'buttons',
          label: '',
          sortable: false
        }
      ]
    },
    group_list_fields() {
      return [
        {
          key: 'name',
          label: this.$t('components.group_card.group_name'),
          sortable: false
        },
        {
          key: 'ou_fq_name',
          label: this.$tc('system.organizational_unit', 1),
          sortable: false
        },
        {
          key: 'description',
          label: this.$t('system.description'),
          sortable: false
        },
        {
          key: 'buttons',
          label: '',
          sortable: false
        }
      ]
    },
    permission_list_fields() {
      return [
        {
          key: 'system',
          label: this.$t('views.netdb.tokens.system')
        },
        {
          key: 'name',
          label: this.$t('views.netdb.tokens.permission')
        }
      ]
    },
    token_list_fields() {
      return [
        {
          key: 'description',
          label: this.$t('system.description'),
          sortable: true
        },
        {
          key: 'expiration_date',
          label: this.$t('views.netdb.tokens.expiration_date'),
          sortable: true
        },
        {
          key: 'delete_date',
          label: this.$t('views.netdb.tokens.deletion_date'),
          sortable: true
        },
        {
          key: 'last_login_date',
          label: this.$t('views.netdb.tokens.last_used'),
          formatter: this.formatDate,
          sortable: true
        },
        {
          key: 'last_generate_date',
          label: this.$t('views.netdb.tokens.last_generated'),
          formatter: this.formatDate,
          sortable: true
        },
        {
          key: 'buttons',
          label: '',
          sortable: false
        }
      ]
    },
  }
}
</script>

<style scoped>

</style>
